// /frontend/src/pages/wishlist/WishlistPage.jsx (Updated with better error handling)
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import { FaArrowLeft, FaShoppingCart, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { wishlistService } from '../../services/wishlistService';
import { cartService } from '../../services/cartService';

const WishlistPage = () => {
  const [wishlist, setWishlist] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  // Fetch wishlist data
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        if (!isAuthenticated) {
          // If not authenticated, redirect to login
          navigate('/login?redirect=/wishlist');
          return;
        }
        
        setLoading(true);
        console.log('Fetching wishlist...');
        const wishlistData = await wishlistService.getWishlist();
        console.log('Wishlist data received:', wishlistData);
        
        setWishlist({
          ...wishlistData,
          items: wishlistData?.items || []
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching wishlist:', err);
        // Provide more detailed error message
        let errorMessage = 'Failed to load wishlist';
        if (err.response) {
          errorMessage += `: ${err.response.status} ${err.response.statusText}`;
        } else if (err.request) {
          errorMessage += ': No response from server';
        } else {
          errorMessage += `: ${err.message}`;
        }
        
        setError(errorMessage);
        setLoading(false);
        // Initialize with empty items to prevent errors
        setWishlist({ items: [] });
      }
    };
    
    fetchWishlist();
  }, [isAuthenticated, navigate]);
  
  // Add to cart
  const handleAddToCart = async (productId) => {
    try {
      setActionLoading(prev => ({ ...prev, [productId]: 'cart' }));
      console.log(`Adding product ${productId} to cart...`);
      await cartService.addToCart(productId, 1);
      console.log(`Product ${productId} added to cart successfully`);
      setActionLoading(prev => ({ ...prev, [productId]: false }));
      // Optionally show success message
    } catch (err) {
      console.error('Error adding to cart:', err);
      setError(`Failed to add item to cart: ${err.message}`);
      setActionLoading(prev => ({ ...prev, [productId]: false }));
    }
  };
  
  // Remove from wishlist
  const handleRemoveFromWishlist = async (productId) => {
    try {
      setActionLoading(prev => ({ ...prev, [productId]: 'remove' }));
      console.log(`Removing product ${productId} from wishlist...`);
      await wishlistService.removeFromWishlist(productId);
      console.log(`Product ${productId} removed from wishlist successfully`);
      
      // Update wishlist state
      setWishlist(prev => ({
        ...prev,
        items: prev.items.filter(item => item._id !== productId)
      }));
      
      setActionLoading(prev => ({ ...prev, [productId]: false }));
    } catch (err) {
      console.error('Error removing from wishlist:', err);
      setError(`Failed to remove item from wishlist: ${err.message}`);
      setActionLoading(prev => ({ ...prev, [productId]: false }));
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-48 bg-gray-200 rounded mb-6"></div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Your Wishlist</h1>
            <Link 
              to="/browse" 
              className="inline-flex items-center text-teal-800 hover:text-teal-900 font-medium"
            >
              <FaArrowLeft className="mr-2" /> Continue Shopping
            </Link>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <FaExclamationTriangle className="text-red-600 mr-4 mt-1" />
              <div>
                <h2 className="text-xl font-semibold text-red-700 mb-2">Error Loading Wishlist</h2>
                <p className="text-red-600 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-center py-16 border border-gray-200 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to display your wishlist</h2>
            <p className="text-gray-600 mb-6">Please try again later or browse our collection.</p>
            <Link 
              to="/browse" 
              className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
            >
              Collection
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Your Wishlist</h1>
          <Link 
            to="/browse" 
            className="inline-flex items-center text-teal-800 hover:text-teal-900 font-medium"
          >
            <FaArrowLeft className="mr-2" /> Continue Shopping
          </Link>
        </div>
        
        {!wishlist.items.length ? (
          <div className="text-center py-16 border border-gray-200 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-600 mb-6">Looks like you haven't added any items to your wishlist yet.</p>
            <Link 
              to="/browse" 
              className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlist.items.map((item) => (
              <div key={item._id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow transition-shadow">
                {/* Product Image */}
                <div className="relative h-48 bg-gray-100">
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        console.log(`Image load error for ${item.name}`);
                        e.target.onerror = null;
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-gray-400">
                      No Image Available
                    </div>
                  )}
                </div>
                
                {/* Product Details */}
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1 truncate">{item.name}</h3>
                  
                  <div className="flex justify-between items-center mb-3">
                    {/* <div className="text-lg font-bold text-gray-900">
                      ${item.price ? item.price.toFixed(2) : '0.00'}
                    </div> */}
                    
                    {item.discountPrice && (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-500 line-through mr-2">${item.discountPrice.toFixed(2)}</span>
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                          Save {Math.round(((item.discountPrice - item.price) / item.discountPrice) * 100)}%
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Rating */}
                  {item.rating && (
                    <div className="flex items-center mb-3">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <svg 
                            key={i} 
                            className={`h-4 w-4 ${i < Math.floor(item.rating) ? 'text-yellow-400' : 'text-gray-300'}`} 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500 ml-1">{item.ratingCount || 0} reviews</span>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex mt-4">
                    <button 
                      onClick={() => handleAddToCart(item._id)}
                      disabled={actionLoading[item._id] === 'cart'}
                      className="flex-1 mr-2 flex items-center justify-center py-2 px-4 bg-teal-800 hover:bg-teal-900 text-white text-sm font-medium rounded transition-colors"
                    >
                      {actionLoading[item._id] === 'cart' ? (
                        <div className="flex items-center">
                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Adding...
                        </div>
                      ) : (
                        <>
                          <FaShoppingCart className="mr-2" />
                          Add to Cart
                        </>
                      )}
                    </button>
                    
                    <button 
                      onClick={() => handleRemoveFromWishlist(item._id)}
                      disabled={actionLoading[item._id] === 'remove'}
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                      title="Remove from wishlist"
                    >
                      {actionLoading[item._id] === 'remove' ? (
                        <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  </div>
                  
                  {/* View Details Link */}
                  <Link 
                    to={`/product/${item._id}`}
                    className="mt-3 block text-center text-sm text-teal-800 hover:text-teal-900 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WishlistPage;